'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

interface Recommendation {
  destination: string;
  description: string;
  price: string;
  duration: string;
  highlights: string[];
  bestFor: string;
  matchScore?: number;
  travelTip?: string;
}

const POPULAR_INTERESTS = [
  { label: '🏖️ Beach & Relaxation', value: 'beach relaxation' },
  { label: '🏛️ Culture & History', value: 'culture history' },
  { label: '🏔️ Adventure & Nature', value: 'adventure nature' },
  { label: '🍽️ Food & Cuisine', value: 'food cuisine' },
  { label: '💼 Business Travel', value: 'business travel' },
  { label: '👨‍👩‍👧 Family Holiday', value: 'family holiday' },
  { label: '🎭 Arts & Entertainment', value: 'arts entertainment' },
  { label: '🧘 Wellness & Spa', value: 'wellness spa' },
];

const BUDGETS = [
  { label: 'Budget (under £500)', value: 'budget under £500' },
  { label: 'Mid-range (£500–£1500)', value: 'mid-range £500-£1500' },
  { label: 'Luxury (£1500+)', value: 'luxury £1500+' },
];

const TRAVEL_DURATIONS = [
  { label: 'Weekend (2-3 days)', value: 'weekend 2-3 days' },
  { label: 'Short break (4-6 days)', value: 'short break 4-6 days' },
  { label: 'Week (7-10 days)', value: 'one week 7-10 days' },
  { label: 'Extended (2+ weeks)', value: 'extended 2+ weeks' },
];

export default function AITravelRecommendations() {
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedBudget, setSelectedBudget] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [travelHistory, setTravelHistory] = useState('');
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    false
  );

  useEffect(() => {
    if (error) toast.error('AI recommendations unavailable. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && hasGenerated) {
      try {
        const jsonMatch = response.match(/\[\s*\{[\s\S]*\}\s*\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          setRecommendations(parsed.slice(0, 3));
        }
      } catch {
        const destinations = response.split(/\d+\./).filter(s => s.trim().length > 20).slice(0, 3);
        const fallback: Recommendation[] = destinations.map((d, i) => ({
          destination: d.split('\n')[0].replace(/[*#]/g, '').trim().substring(0, 40) || `Destination ${i + 1}`,
          description: d.replace(/[*#]/g, '').trim().substring(0, 120) + '...',
          price: selectedBudget.includes('budget') ? 'From £299' : selectedBudget.includes('luxury') ? 'From £1,599' : 'From £699',
          duration: selectedDuration.includes('weekend') ? '3 nights' : '7 nights',
          highlights: ['Curated experience', 'Expert guidance', 'Best value'],
          bestFor: selectedInterests[0] || 'All travellers',
          matchScore: 85 + i * 3,
          travelTip: 'Book early for best prices',
        }));
        setRecommendations(fallback);
      }
    }
  }, [response, isLoading, hasGenerated, selectedBudget, selectedDuration, selectedInterests]);

  const toggleInterest = (value: string) => {
    setSelectedInterests(prev =>
      prev.includes(value) ? prev.filter(i => i !== value) : prev.length < 3 ? [...prev, value] : prev
    );
  };

  const handleGetRecommendations = () => {
    if (selectedInterests.length === 0 || !selectedBudget) {
      toast.error('Please select at least one travel interest and your budget.');
      return;
    }
    setHasGenerated(true);
    setRecommendations([]);

    const historyContext = travelHistory.trim() ? `\nTravel History & Preferences: ${travelHistory}` : '';
    const durationContext = selectedDuration ? `\nPreferred Trip Duration: ${selectedDuration}` : '';

    const prompt = `You are an expert UK travel advisor. Generate exactly 3 highly personalised travel recommendations.

Travel Interests: ${selectedInterests.join(', ')}
Budget: ${selectedBudget}${durationContext}${historyContext}

Return ONLY a valid JSON array with exactly 3 objects. Each object must have these exact fields:
- destination (string: specific city/country)
- description (string: 2-3 sentence personalised description)
- price (string: e.g. "From £499 pp")
- duration (string: e.g. "7 nights")
- highlights (array of exactly 3 short strings specific to their interests)
- bestFor (string: who this suits)
- matchScore (number: 80-99)
- travelTip (string: one insider tip)

Return ONLY the JSON array, no other text.`;

    sendMessage([{ role: 'user', content: prompt }], { temperature: 0.7, max_tokens: 1500 });
  };

  const canGenerate = selectedInterests.length > 0 && selectedBudget;

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-white to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full border border-primary/20 mb-4">
            <Icon name="SparklesIcon" size={18} className="text-primary" />
            <span className="text-sm font-black text-primary">Anthropic AI-Powered</span>
            <span className="text-xs bg-trust-green text-white px-2 py-0.5 rounded-full font-bold">Personalised</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground mb-4">
            Your Personalised Travel Recommendations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tell us your travel style, budget, and history — our AI crafts recommendations uniquely tailored to you.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-border p-8 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="font-heading font-black text-foreground mb-1 flex items-center gap-2">
                <Icon name="HeartIcon" size={20} className="text-accent" />
                Travel Interests
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Select up to 3 interests</p>
              <div className="grid grid-cols-2 gap-2">
                {POPULAR_INTERESTS.map((interest) => (
                  <button
                    key={interest.value}
                    onClick={() => toggleInterest(interest.value)}
                    disabled={selectedInterests.length >= 3 && !selectedInterests.includes(interest.value)}
                    className={`px-3 py-2.5 rounded-xl text-sm font-bold border-2 transition-all duration-200 text-left relative ${
                      selectedInterests.includes(interest.value)
                        ? 'bg-primary text-white border-primary shadow-md'
                        : selectedInterests.length >= 3
                        ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' :'bg-white text-foreground border-border hover:border-primary/50 hover:bg-primary/5'
                    }`}
                  >
                    {interest.label}
                    {selectedInterests.includes(interest.value) && (
                      <span className="absolute top-1 right-1 w-4 h-4 bg-white/30 rounded-full flex items-center justify-center">
                        <Icon name="CheckIcon" size={10} className="text-white" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-black text-foreground mb-3 flex items-center gap-2">
                  <Icon name="CurrencyPoundIcon" size={20} className="text-trust-green" />
                  Budget
                </h3>
                <div className="flex flex-col gap-2">
                  {BUDGETS.map((budget) => (
                    <button
                      key={budget.value}
                      onClick={() => setSelectedBudget(budget.value)}
                      className={`px-4 py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 text-left ${
                        selectedBudget === budget.value
                          ? 'bg-trust-green text-white border-trust-green shadow-md'
                          : 'bg-white text-foreground border-border hover:border-trust-green/50 hover:bg-trust-green/5'
                      }`}
                    >
                      {budget.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-heading font-black text-foreground mb-3 flex items-center gap-2">
                  <Icon name="CalendarDaysIcon" size={20} className="text-secondary" />
                  Trip Duration
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {TRAVEL_DURATIONS.map((dur) => (
                    <button
                      key={dur.value}
                      onClick={() => setSelectedDuration(selectedDuration === dur.value ? '' : dur.value)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all duration-200 text-left ${
                        selectedDuration === dur.value
                          ? 'bg-secondary text-white border-secondary' :'bg-white text-foreground border-border hover:border-secondary/50'
                      }`}
                    >
                      {dur.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-bold text-primary hover:text-secondary transition-colors mb-4"
            >
              <Icon name={showAdvanced ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} />
              {showAdvanced ? 'Hide' : 'Add'} Travel History & Preferences (optional)
            </button>
            {showAdvanced && (
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">
                  Where have you travelled before? What did you love or dislike?
                </label>
                <textarea
                  value={travelHistory}
                  onChange={(e) => setTravelHistory(e.target.value)}
                  placeholder="e.g. Loved Japan for the food and culture, found Paris too crowded. Prefer off-the-beaten-path destinations..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">This helps our AI personalise recommendations based on your actual travel history</p>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handleGetRecommendations}
              disabled={isLoading || !canGenerate}
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-primary to-secondary text-white font-heading font-black text-lg rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  AI is crafting your recommendations...
                </>
              ) : (
                <>
                  <Icon name="SparklesIcon" size={22} />
                  Get My Personalised Recommendations
                </>
              )}
            </button>
            {!canGenerate && (
              <p className="text-sm text-muted-foreground mt-2">Select at least one interest and your budget to continue</p>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded-lg mb-3 w-3/4" />
                <div className="h-4 bg-gray-100 rounded mb-2" />
                <div className="h-4 bg-gray-100 rounded mb-2 w-5/6" />
                <div className="h-4 bg-gray-100 rounded w-4/6 mb-4" />
                <div className="flex gap-2">
                  <div className="h-6 bg-gray-100 rounded-full w-20" />
                  <div className="h-6 bg-gray-100 rounded-full w-24" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && recommendations.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Icon name="CheckCircleIcon" size={20} className="text-trust-green" />
              <p className="text-sm font-bold text-trust-green">
                AI generated {recommendations.length} personalised recommendations based on your profile
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl border-2 border-border hover:border-primary/40 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="bg-gradient-to-br from-primary to-secondary p-6 text-white relative">
                    {rec.matchScore && (
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                        <span className="text-xs font-black text-white">{rec.matchScore}% match</span>
                      </div>
                    )}
                    <div className="flex items-start mb-2 pr-16">
                      <h3 className="font-heading font-black text-xl leading-tight">{rec.destination}</h3>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full font-bold">{rec.duration}</span>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">{rec.description}</p>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-2xl font-heading font-black text-foreground">{rec.price}</p>
                        <p className="text-xs text-muted-foreground">per person, flights included</p>
                      </div>
                      <p className="text-xs font-bold text-trust-green bg-trust-green/10 px-2 py-1 rounded-full">{rec.bestFor}</p>
                    </div>

                    <div className="space-y-2 mb-4">
                      {rec.highlights.map((h, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Icon name="CheckCircleIcon" size={14} className="text-trust-green mt-0.5 flex-shrink-0" variant="solid" />
                          <span className="text-sm text-foreground">{h}</span>
                        </div>
                      ))}
                    </div>

                    {rec.travelTip && (
                      <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <Icon name="LightBulbIcon" size={14} className="text-accent mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-foreground font-medium">{rec.travelTip}</p>
                        </div>
                      </div>
                    )}

                    <a
                      href="/itinerary-gallery"
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white font-heading font-black rounded-xl hover:shadow-lg transition-all duration-300 group-hover:scale-105"
                    >
                      <Icon name="MapIcon" size={16} />
                      Explore Itineraries
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <button
                onClick={handleGetRecommendations}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary text-primary font-bold rounded-xl hover:bg-primary hover:text-white transition-all duration-300"
              >
                <Icon name="ArrowPathIcon" size={18} />
                Regenerate Recommendations
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
